import { message } from 'antd'
import { useLingui } from '@lingui/react/macro'

import { TTS_MAX_TEXT_LENGTH, TTS_MIN_TEXT_LENGTH } from '../../../_constant'
import { useTTSStore } from '../../../_store/useTTSStore'

import type { TTSFormValues } from '../../../_types/form'

import { EFeatureName, useFeatureGuard } from '@/hooks/usageValidation'
import { api } from '@/utils/api'
import { useTrackCommonParams } from '@/hooks/analyticsData/trackCommonParams'

export function useTTSSubmit(options?: { onSuccess?: () => void }) {
  const { t } = useLingui()
  const setUI = useTTSStore((s) => s.setUI)
  const guardGenerate = useFeatureGuard(EFeatureName.Generate, {
    triggerMethod: 'text_to_speech_check',
    signUpParams: { triggerLocation: 'generate_tts', triggerType: 'create' },
  })
  const getCommonParamsRef = useTrackCommonParams()
  const { mutateAsync: createTask } = api.tts.createTask.useMutation({
    onSuccess: () => {
      message.success(t`TTS task created successfully!`)
      options?.onSuccess?.()
    },
  })

  const handleSubmit = guardGenerate(async (formValues: TTSFormValues) => {
    const hasEmptyText = formValues.dialogue.some((d) => !d.text.trim())
    if (hasEmptyText) {
      message.error(t`Please enter text for all dialogues`)
      return
    }

    const hasEmptyVoice = formValues.dialogue.some((d) => !d.voice)
    if (hasEmptyVoice) {
      message.error(t`Please select a voice for all dialogues`)
      return
    }

    const totalChars = formValues.dialogue.reduce(
      (sum, d) => sum + d.text.length,
      0,
    )
    if (totalChars > TTS_MAX_TEXT_LENGTH) {
      return
    }
    if (totalChars < TTS_MIN_TEXT_LENGTH) {
      message.error(
        t`Please enter at least ${TTS_MIN_TEXT_LENGTH} characters for your text input.`,
      )
      return
    }

    try {
      setUI({ isSubmitting: true })
      const { commonParams: trackingContext } =
        await getCommonParamsRef.current()
      await createTask({
        dialogue: formValues.dialogue,
        stability: formValues.stability as 0 | 0.5 | 1,
        speech_language: formValues.speech_language,
        trackingContext,
      })
    } catch {
      // ignore
    } finally {
      setUI({ isSubmitting: false })
    }
  })

  return {
    handleSubmit,
  }
}
